import { Injectable } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker'
import { DynamicDialogRef } from 'primeng/dynamicdialog'
import { filter, first } from 'rxjs'
import { DialogService } from './dialog.service'
import * as Sentry from '@sentry/angular'
import { UserService } from './user.service'
import { environment } from '@env/environment'

export interface SwAppData {
    applicationVersion: string
    // The version below which all clients must update (e.g., '1.7.0')
    minRequiredVersion?: number | null
    // Specific versions that are known to be buggy (e.g., ['1.6.2', '1.5.8'])
    deprecatedVersions?: number[]
    // Flag for optional updates (e.g., if true, show a dismissible prompt)
    promptUser?: boolean
}

@Injectable({ providedIn: 'root' })
export class SoftwareUpdateService {
    /** The update prompt that is open, or null. */
    private promptRef: DynamicDialogRef | null = null
    private isPromptMandatory = false
    /** The prompt waiting for the user to leave a session: its isMandatory, or null when none waits. */
    private heldPrompt: boolean | null = null

    constructor(
        private swUpdate: SwUpdate,
        private dialogService: DialogService,
        private userService: UserService,
        private router: Router,
    ) {
        if (this.swUpdate.isEnabled) {
            this.registerUpateEvents()
            this.swUpdate.checkForUpdate()
        }
        this.holdPromptDuringSessions()
    }

    private shouldForceUpdate(appData: SwAppData): boolean {
        // Extract the integer portion of the version number. Ex. 851.a58159fb -> 851
        // This is gauranteed to be unique for production versions and incremental
        const currentVersion: number = +environment.applicationVersion.split('.')[0]
        const { minRequiredVersion, deprecatedVersions } = appData

        // Rule 1: Check against the Minimum Required Version
        if (minRequiredVersion) {
            if (currentVersion < minRequiredVersion) {
                console.log(
                    `MANDATORY: Current version (${currentVersion}) is below minimum required (${minRequiredVersion}).`,
                )
                return true
            }
        }

        // Rule 2: Check against the Specific Deprecated Versions
        if (deprecatedVersions && deprecatedVersions.length > 0) {
            if (deprecatedVersions.includes(currentVersion)) {
                console.log(
                    `MANDATORY: Current version (${currentVersion}) is on the deprecated list.`,
                )
                return true
            }
        }

        return false
    }

    public registerUpateEvents() {
        this.swUpdate.versionUpdates
            .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
            .subscribe((evt) => {
                const appData = evt.latestVersion.appData as SwAppData

                if (!appData || !appData.applicationVersion) {
                    console.error('Service Worker manifest is missing application version data.')
                    return
                }

                const isMandatory = this.shouldForceUpdate(appData)

                this.userService.isAuthenticated.pipe(first()).subscribe((authenticated) => {
                    // Only prompt authenticated users
                    if (authenticated && (isMandatory || appData.promptUser)) {
                        this.showPrompt(isMandatory)
                    }
                })
            })

        this.swUpdate.unrecoverable.subscribe((event) => {
            Sentry.withScope((scope) => {
                scope.setExtras({ reason: event.reason, type: event.type })
                Sentry.captureMessage('Service Worker Unrecoverable State')
            })
        })
    }

    /**
     * Open the update prompt, or hold it until the user leaves a session.
     *
     * Updating reloads the page, which would drop a live call, so the prompt never shows in one.
     */
    private showPrompt(isMandatory: boolean): void {
        if (this.isInFocusmateSession()) {
            this.heldPrompt = (this.heldPrompt ?? false) || isMandatory
            return
        }
        if (this.promptRef) {
            return
        }
        const ref = this.dialogService.openSWUpdateNotification(isMandatory)
        this.promptRef = ref
        this.isPromptMandatory = isMandatory
        ref?.onClose.pipe(first()).subscribe(() => {
            if (this.promptRef === ref) {
                this.promptRef = null
            }
        })
    }

    /**
     * Close the prompt when the user enters a session and reopen it when they leave.
     *
     * A prompt the user dismissed with Later stays dismissed.
     */
    private holdPromptDuringSessions(): void {
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(() => {
                if (this.isInFocusmateSession()) {
                    if (this.promptRef) {
                        const ref = this.promptRef
                        this.promptRef = null
                        this.heldPrompt = (this.heldPrompt ?? false) || this.isPromptMandatory
                        ref.close()
                    }
                    return
                }
                if (this.heldPrompt !== null) {
                    const isMandatory = this.heldPrompt
                    this.heldPrompt = null
                    this.showPrompt(isMandatory)
                }
            })
    }

    /**
     * True on live session routes, or before the first navigation, when the route is unknown.
     *
     * Matches ForcedReloadService.isInFocusmateSession on the cookie-banner branch;
     * use that one once both are merged.
     */
    private isInFocusmateSession(): boolean {
        if (!this.router.navigated) {
            return true
        }
        const path = this.router.url.split('?')[0].split('#')[0]
        // `/sessions` is the list page; `/session/:id` and `/session/g/:id` are live rooms.
        return path.startsWith('/session/')
    }

    // Public method to expose update check (used for manual checks or timers)
    public checkForUpdate(): void {
        this.swUpdate.isEnabled && this.swUpdate.checkForUpdate().then(() => {})
    }
}
